import { useAuthStore } from './auth';

// The ONE place the RBAC level ordering + admin-bypass live on the frontend
// (R-088). Backends enforce authoritatively (D-061); this drives UX gating only
// — what a user sees and can act on. Mirrors `wc_auth::Level`.
export type PermissionLevel = 'read' | 'write' | 'approve' | 'admin';

const LEVEL_ORDER: Record<PermissionLevel, number> = { read: 1, write: 2, approve: 3, admin: 4 };

/** Pure check — usable outside React (e.g. building the manifest registry context). */
export function canWith(
  userType: string | undefined,
  permissions: Record<string, string> | undefined,
  module: string,
  min: PermissionLevel,
): boolean {
  if (userType === 'admin') return true; // admin bypasses all role checks (D-030)
  const held = permissions?.[module] as PermissionLevel | undefined;
  return held != null && (LEVEL_ORDER[held] ?? 0) >= LEVEL_ORDER[min];
}

export interface Permissions {
  /** True when the current user's resolved level for `module` is at least `min`. */
  can: (module: string, min: PermissionLevel) => boolean;
  /** Admins bypass every role/permission check. */
  isAdmin: boolean;
}

/**
 * Canonical permission accessor for the SPA. One source of truth for "may this
 * user see / do X" — used by the router (to build the feature registry) and by
 * any page gating an action. Never invent ad-hoc `permissions[x] >= y` checks;
 * call this.
 */
export function usePermissions(): Permissions {
  const userType = useAuthStore((s) => s.user?.userType);
  const permissions = useAuthStore((s) => s.user?.permissions);
  return {
    can: (module, min) => canWith(userType, permissions, module, min),
    isAdmin: userType === 'admin',
  };
}
